import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import Select from "react-select";
import ProfileSectionCard from "./ProfileSectionCard";
import UserService from "services/user.service";
import AuthService from "services/auth.service";
import { FileTextOutlined } from "@ant-design/icons";

const UPLOAD_DOCUMENTS = [
  {
    key: "apc",
    label: "APC",
    profileKey: "apc",
    fileKey: "files[apc]",
    removeKey: "attributes[remove_apc_url]",
    defaultName: "View APC",
  },
  {
    key: "prd",
    label: "MMC/MDC Registration Document",
    profileKey: "professional_registration_document",
    fileKey: "files[professional_registration_document]",
    removeKey: "attributes[remove_professional_registration_document_url]",
    defaultName: "View Professional Registration Document",
  },
  {
    key: "resume",
    label: "Resume/CV",
    profileKey: "resume_document",
    fileKey: "files[resume_document]",
    removeKey: "attributes[remove_resume_document_url]",
    defaultName: "View Resume",
  },
];

const BackgroundInformation = ({ onProfileRefresh, profile, isReadOnly = false }) => {
  const [summary, setSummary] = useState("");
  const [qualifications, setQualifications] = useState([""]);
  const [mmc, setMmc] = useState("");
  const [nsr, setNsr] = useState("");
  const [selectedSpecialty, setSelectedSpecialty] = useState([]);
  const [SpecialtiesList, setSpecialtiesList] = useState([]); // to get list of specialties
  
  const [files, setFiles] = useState({
    apc: { file: null, isEmpty: false },
    prd: { file: null, isEmpty: false },
    resume: { file: null, isEmpty: false },
  });

  const isPendingReview = profile?.pending_sections?.credentials;

  useEffect(() => {
    UserService.getSpecialties()
      .then((response) => {
        console.log("Full API Response", response);
        const list = Array.isArray(response)
        ? response
        : response?.specialties || [];
        setSpecialtiesList(list);
      }).catch((error) => {
        console.error("Failed to load specialties list:", error);
      });
  }, []);

  const resetForm = () => {
    setSummary(profile?.summary || "");
    setQualifications(profile?.qualifications?.length > 0
      ? profile.qualifications.map((q) => q.name)
      : [""]
    );
    setMmc(profile?.mmc || "");
    setNsr(profile?.nsr || "");
    setFiles({
      apc: { file: null, isEmpty: false },
      prd: { file: null, isEmpty: false },
      resume: { file: null, isEmpty: false },
    });

    if (profile?.specialties?.length > 0) {
      const ids = profile.specialties.map((s) =>
        typeof s === "object" ? s.id : s
      );
      setSelectedSpecialty(ids);
    } else {
      setSelectedSpecialty([]);
    }
  };

  useEffect(() => {
    resetForm();
  }, [profile]);

  const handleBeforeEdit = () => {
    if (isPendingReview) {
      toast.warning("You already have an edit pending admin review. Please wait for approval.");
      return false; // Blocks opening edit mode
    }
    return true;
  };

  const validateInput = () => {
    if (!summary.trim()) {
      toast.error("Professional summary cannot be empty.");
      return false;
    }

    // Check for empty qualification fields
    for (let i = 0; i < qualifications.length; i++) {
      if (!qualifications[i].trim()) {
        toast.error(`Qualification ${i + 1} cannot be empty!`);
        return false;
      }
    }

    // Check for duplicates
    const cleanedQualifications = qualifications.map((item) => item.trim().toLowerCase());
    if (cleanedQualifications.some((q, idx) => cleanedQualifications.indexOf(q) !== idx)) {
      toast.error("Duplicate qualifications detected.");
      return false;
    }

    // Validation for react-select: check if the array is empty
    if (selectedSpecialty.length === 0) {
      toast.error("Please select at least one specialty.");
      return false;
    } 
    return true;
  };

  const handleSave = async () => {
    if (!validateInput()) {
      throw new Error("Validation failed");
    }

    const initialSummary = profile?.summary || "";
    const initialMmc = profile?.mmc || "";
    const initialNsr = profile?.nsr || "";

    const initialQualifications = profile?.qualifications?.length > 0
      ? profile.qualifications.map((q) => (typeof q === "object" ? q.name : String(q)).trim()).filter(Boolean)
      : [""];
    const currentQualifications = qualifications.map((q) => q.trim()).filter(Boolean);
    const formattedQualifications = currentQualifications.join("||");
    const initialQualificationsStr = initialQualifications.join("||");

    const initialSpecialties = profile?.specialties?.length > 0
      ? profile.specialties.map((s) => String(typeof s === "object" ? s.id : s)).sort().join(",")
      : "";
    const currentSpecialtiesStr = [...selectedSpecialty].map(String).sort().join(",");

    const fileChanged = UPLOAD_DOCUMENTS.some(
      (document) => files[document.key].file !== null || files[document.key].isEmpty
    );

    const isChanged =
      fileChanged ||
      summary.trim() !== initialSummary.trim() ||
      mmc.trim() !== initialMmc.trim() ||
      nsr.trim() !== initialNsr.trim() ||
      formattedQualifications !== initialQualificationsStr ||
      currentSpecialtiesStr !== initialSpecialties;

    if (!isChanged) {
      resetForm();
      return;
    }

    const formData = new FormData();
    formData.append('request_type', 'credentials');
    formData.append('attributes[mmc]', mmc.trim());
    formData.append('attributes[nsr]', nsr.trim());
    formData.append('attributes[summary]', summary.trim());

    selectedSpecialty
      .forEach((id) => {
        formData.append("attributes[specialty_ids][]", id);
      });
    
    qualifications.forEach(qualificationName => {
      formData.append('attributes[qualification_names][]', qualificationName.trim());
    });

    UPLOAD_DOCUMENTS.forEach(({key, fileKey, removeKey}) => {
      const state = files[key];
      if (state.file) {
        formData.append(fileKey, state.file);
      } else if (state.isEmpty) {
        formData.append(removeKey, "1");
      }
    });

    try {
      await UserService.submitProfileChangeRequest(formData);
      toast.success("Changes saved and submitted for admin approval!");
      if (onProfileRefresh) {
        await onProfileRefresh();
      }
    } catch (error) {
      if (error.response?.status === 401){
        AuthService.logout();
        window.location.replace("/eb/login");
      } else {
        toast.error(error.response?.data?.errors?.join(',') || "Failed to submit changes.")
      }
      throw error;
    }
  };

  const specialtyOptions = SpecialtiesList.map((spec) => ({
    value: spec.id,
    label: spec.name,
  }));

  // Find the full option objects that match the selected IDs
  const selectedSpecialtyOptions = selectedSpecialty
    .map((id) => {
      if (!id) return null;
      const matchedSpec = SpecialtiesList.find((s) => String(s.id) === String(id));
      const profileSpec = profile?.specialties?.find((s) => String(typeof s === "object" ? s.id : s) === String(id));
      const labelName = matchedSpec?.name || profileSpec?.name || "Unknown";
      return { value: id, label: labelName };
    }).filter(Boolean);

  const handleSpecialtyChange = (selectedItems) => {
    const newNames = selectedItems ? selectedItems.map((item) => item.value) : [];
    setSelectedSpecialty(newNames);
  };

  const handleQualificationChange = (index, value) => {
    const updated = [...qualifications];
    updated[index] = value;
    setQualifications(updated);
  };

  const handleAddQualification = () => {
    setQualifications([...qualifications, ''])
  };

  const handleDeleteQualification = (index) => {
    const updated = qualifications.filter((_, i) => i !== index);
    setQualifications(updated.length === 0 ? [''] : updated);
  };

  // File related Handlers
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + '' + sizes[i];
  };

  const getFileInfo = (profile, defaultName) => {
    const url = profile?.styles?.origin?.url;
    const filename = profile?.original_filename || (url ? url.split("/").pop().split("?")[0] : defaultName);
    return { url, filename, size: profile?.file_size };
  };

  const handleFileChange = (key, e, label) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 4 * 1024 * 1024) {
        toast.warning(`${label} size should not exceed 4MB.`);
        return;
      }
      setFiles((prev) => ({ ...prev, [key]: { file, isEmpty: false } }));
    }
  };

  const handleClearFile = (key) => {
    setFiles((prev) => ({ ...prev, [key]: { file: null, isEmpty: true } }));
  };

  const validQualifications = qualifications.filter(q => q.trim());
  const specialtyNames = profile?.specialties?.map((s) => typeof s === "object" ? s.name : String(s)).filter(Boolean) || [];

  const customSelectStyles = {
    control: (provided) => ({
      ...provided,
      padding: "0.375rem 0.75rem",
      borderColor: "#ced4da",
      boxShadow: "none",
      "&:hover": {
        borderColor: "#80bdff",
      },
    }),
  };

  return (
    <ProfileSectionCard
      title="Background"
      showEditButton={!isReadOnly}
      onBeforeEdit={handleBeforeEdit}
      onSave={handleSave}
      onCancel={resetForm}
    >
      {({isEditing}) => (
        <div className="profile-info-group">
          {/* READ ONLY MODE */}
          {!isEditing && (
            <>
              <div className="profile-info-group">
                <p className="sub-header">CREDENTIALS</p>
                <div className="profile-info-row mb-4">
                  <h6 className="profile-info-label">Medical Registration No.</h6>
                  <p className="profile-info-value">{mmc || "-"}</p>
                </div>
                <div className="profile-info-row mb-4">
                  <h6 className="profile-info-label">National Specialist Registry No.</h6>
                  <p className="profile-info-value">{nsr || "-"}</p>
                </div>
                <div className="profile-info-row mb-4">
                  <h6 className="profile-info-label">Qualifications</h6>
                  <div className="profile-info-value">
                    {validQualifications.length > 0 ? (
                      validQualifications.length === 1 ? (
                        <p className="profile-info-value mb-0">{validQualifications[0]}</p>
                      ) : (
                        <ul className="mb-0 pl-3">
                          {validQualifications.map((q, index) => (
                            <li key={index} className="profile-info-value">{q}</li>
                          ))}
                        </ul>
                      )
                    ) : (
                      <p className="profile-info-value">-</p>
                    )}
                  </div>
                </div>
              </div>
              {UPLOAD_DOCUMENTS.map((document) => {
                const info = getFileInfo(profile?.[document.profileKey], document.defaultName);
                return (
                  <div className="profile-info-row" key={`view-${document.key}`}>
                    <h6 className="profile-info-label">{document.label}</h6>
                    <p className="profile-info-value">
                      {info.url ? (
                        <a href={info.url} target="_blank" rel="noopener noreferrer" className="photo-link">
                          {info.filename}
                        </a>
                      ) : (
                        <span className="text-muted">No file uploaded</span>
                      )}
                    </p>
                  </div>
                );
              })}
              <div className="profile-info-group">
                <p className="sub-header">SPECIALTIES</p>
                <div className="profile-info-row">
                  <h6 className="profile-info-label">Specialties</h6>
                  <div className="profile-info-value">
                    {specialtyNames.length > 0 ? (
                      specialtyNames.length === 1 ? (
                        <p className="profile-info-value mb-0">{specialtyNames[0]}</p>
                      ) : (
                        <ul className="mb-0 pl-3">
                          {specialtyNames.map((s, index) => (
                            <li key={index} className="profile-info-value">{s}</li>
                          ))}
                        </ul>
                      )
                    ) : (
                      <p className="profile-info-value">-</p>
                    )}
                  </div>
                </div>
              </div>
              <div className="profile-info-group">
                <p className="sub-header">SUMMARY</p>
                <div className="profile-info-row">
                  <h6 className="profile-info-label">Professional Summary</h6>
                  <p className="profile-info-value">{summary || "-"}</p>
                </div>
              </div>
            </>
          )}
          {/* EDIT MODE */}
          {isEditing && (
            <div className="profile-info-group">
              <p className="sub-header">CREDENTIALS</p>
              {/* Edit Credentials */}
              <div className="profile-info-row">
                <label className="profile-info-label">Medical Registration No.</label>
                <input
                  type="text"
                  className="form-control py-6 px-4 profile-info-value"
                  value={mmc}
                  onChange={(e) => setMmc(e.target.value)}/>
              </div>
              <div className="profile-info-row">
                <label className="profile-info-label">National Specialist Registry No.</label>
                <input
                  type="text"
                  className="form-control py-6 px-4 w-100 profile-info-value"
                  value={nsr}
                  onChange={(e) => setNsr(e.target.value)}/>
              </div>
              <div className="profile-info-row mb-4">
                <label className="profile-info-label">Qualifications</label>
                <div className="flex-grow-1">
                  {qualifications.map((q, index) => (
                  <div className="d-flex align-items-center mb-3 input-wrapper" key={index}>
                    <input
                      type="text"
                      value={q}
                      placeholder="e.g. MBBS (Malaya)"
                      onChange={(e) => handleQualificationChange(index, e.target.value)}
                      className="form-control profile-info-value py-6 px-4 w-100"
                      autoFocus={index === qualifications.length - 1}
                    />
                    {qualifications.length > 1 && (
                      <button
                        type="button"
                        className="delete-btn"
                        onClick={() => handleDeleteQualification(index)}
                        style={{ lineHeight: 1, fontSize: '1.75rem'}}
                      >
                        &times;
                      </button>
                    )}
                  </div>
                ))}
                <button
                    type="button"
                    className="add-btn"
                    onClick={handleAddQualification}
                  >
                  + Add Qualification
                </button>
                </div>
              </div>

              {/* Upload Documents */}
              {UPLOAD_DOCUMENTS.map((document) => {
                const state = files[document.key];
                const existingInfo = getFileInfo(profile?.[document.profileKey], document.defaultName);
                
                const hasPresent = !!state.file || (!!existingInfo.url && !state.isEmpty);
                const displayFileName = state.file ? state.file.name : (hasPresent ? existingInfo.filename : null);
                const displayFileSize = state.file 
                  ? formatFileSize(state.file.size) 
                  : (hasPresent && existingInfo.size) 
                    ? formatFileSize(existingInfo.size) 
                    : "PDF, JPG, JPEG, PNG (Max. 4MB)";

                return (
                  <div className="profile-info-row" key={`edit-${document.key}`}>
                    <label className="profile-info-label">{document.label}</label>
                    <div className="profile-info-value">
                      <div className="file-upload-box mb-3">
                        <div className="d-flex align-items-center">
                          {hasPresent && (
                            <div className="me-3" style={{ marginRight: "16px" }}>
                              <FileTextOutlined className="text-danger" style={{ fontSize: "24px" }} />
                            </div>
                          )}
                          <div>
                            <div className="text-dark font-weight-medium">
                              {displayFileName ? displayFileName : "Upload your file here"}
                            </div>
                            <small className="text-muted">{displayFileSize}</small>
                          </div>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <label className={`mb-0 cursor-pointer ${hasPresent ? "replace-file-btn" : "browse-file-btn"}`}>
                            {hasPresent ? "Replace" : "Browse File"}
                            <input
                              type="file"
                              accept=".pdf,.jpg,.jpeg,.png"
                              className="d-none"
                              onChange={(e) => handleFileChange(document.key, e, document.label)}
                              onClick={(e) => { e.target.value = null; }}
                            />
                          </label>
                          {hasPresent && (
                            <button
                              type="button"
                              className="clear-file-btn"
                              style={{ paddingLeft: "15px" }}
                              onClick={() => handleClearFile(document.key)}
                              title="Remove file"
                            >
                              &times;
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
              
              {/* Edit Specialties */}
              <div className="profile-info-group">
                <p className="sub-header">SPECIALTIES</p>
                <div className="profile-info-row">
                  <label className="profile-info-label">Specialties</label>
                  <div className="flex-grow-1">
                    <Select
                      isMulti
                      name="specialties"
                      options={specialtyOptions}
                      className="basic-multi-select profile-info-value"
                      classNamePrefix="select"
                      placeholder="Search and select specialties"
                      value={selectedSpecialtyOptions}
                      onChange={handleSpecialtyChange}
                      styles={customSelectStyles}
                      noOptionsMessage={() => "No specialties found"}
                      />
                      <small className="text-muted mt-1 d-block">
                        Type to search and select multiple specialties.
                      </small>
                  </div>
                </div>
              </div>
              
              {/* Edit Summary */}
              <div className="profile-info-group">
                <p className="sub-header">SUMMARY</p>
                <div className="profile-info-row">
                  <label className="profile-info-label">Professional Summary</label>
                  <textarea
                    value={summary}
                    onChange={(e) => setSummary(e.target.value.slice(0, 350))}
                    className="form-control profile-info-value"
                    style={{ minHeight: "150px"}}
                    rows="5"
                    placeholder="Write a short professional summary that patients will see on your profile. Briefly describe your experience and specialties."
                  />
                </div>
                <div className="d-flex justify-content-end mt-1">
                    <small className="text-muted">
                      {summary.length}/350 characters
                    </small>
                  </div>
              </div>
            </div>
          )}
        </div>
      )}
    </ProfileSectionCard>
  );
};

export default BackgroundInformation;